/**
 * Journal media. Attach a photo, record a voice note, add a clip: each one
 * uploads the moment it is picked, waits in a pending strip with its own
 * progress, and comes back on the saved entry with its transcript.
 *
 * app.js owns the journal entry itself. It reads pendingAttachments() when the
 * player saves, calls clearPending() after, and paints attachmentsHtml(entry)
 * inside the list before handing the list to bindEntries().
 */

let ctx = null;
let root = null, strip = null, recBar = null, timerEl = null;
let pending = [];   // { uid, kind, name, state, pct, desc, error }
let rec = null;     // the live recording, or null

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const MAX_BYTES = 20 * 1024 * 1024;
const LABEL = { photo: 'Photo', audio: 'Voice note', video: 'Video' };

const fmtBytes = (b) => (b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);
const fmtDur = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const newId = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
/** The bytes are served by file name under the player's own prefix. */
const fileOf = (d) => String(d?.key || '').split('/').pop() || String(d?.id || '');
const mediaUrl = (d) => `/api/media/${encodeURIComponent(fileOf(d))}`;

/* -------------------------------------------------------------------- mount */

export function mount(c) {
  ctx = c;
  root = document.getElementById('journalMediaRoot');
  if (!root) return;
  root.innerHTML = `
    <div class="jm-row">
      <button class="btn jm-btn" id="jmPhoto">Add photo</button>
      <button class="btn jm-btn" id="jmRecord">Record audio</button>
      <button class="btn jm-btn" id="jmVideo">Add video</button>
      <span class="jm-hint">Up to 20 MB each. Voice notes and clips get written out for you.</span>
    </div>
    <div class="jm-rec" id="jmRecBar" hidden>
      <i class="jm-dot"></i><b id="jmTimer">0:00</b>
      <span class="jm-hint">Recording. Say what practice felt like.</span>
      <button class="btn jm-stop" id="jmStop">Stop</button>
    </div>
    <div class="jm-strip" id="jmStrip"></div>
    <input type="file" id="jmPhotoIn" accept="image/*" hidden>
    <input type="file" id="jmVideoIn" accept="video/*" hidden>`;

  strip = document.getElementById('jmStrip');
  recBar = document.getElementById('jmRecBar');
  timerEl = document.getElementById('jmTimer');

  const photoIn = document.getElementById('jmPhotoIn');
  const videoIn = document.getElementById('jmVideoIn');
  document.getElementById('jmPhoto').onclick = () => photoIn.click();
  document.getElementById('jmVideo').onclick = () => videoIn.click();
  document.getElementById('jmRecord').onclick = startRecording;
  document.getElementById('jmStop').onclick = () => endRecording(false);
  photoIn.onchange = () => { add(photoIn.files?.[0], 'photo'); photoIn.value = ''; };
  videoIn.onchange = () => { add(videoIn.files?.[0], 'video'); videoIn.value = ''; };

  // The microphone is let go the moment the player leaves the journal.
  const view = document.getElementById('v-journal');
  if (view && 'MutationObserver' in window) {
    new MutationObserver(() => { if (!view.classList.contains('on')) endRecording(true); })
      .observe(view, { attributes: true, attributeFilter: ['class'] });
  }
  window.addEventListener('pagehide', () => endRecording(true));
}

/* ------------------------------------------------------------------ upload */

async function add(file, kind) {
  if (!file || !strip) return;
  // api.js keeps the first six attachments on an entry, so stop there rather than lose one.
  if (pending.length >= 6) { ctx.toast('Six attachments per entry is the limit.'); return; }
  if (file.size > MAX_BYTES) { ctx.toast(`That file is ${fmtBytes(file.size)}. The cap is 20 MB.`, true); return; }
  const item = { uid: newId(), kind, name: file.name || `${LABEL[kind] || 'File'}`, state: 'uploading', pct: 0, desc: null, error: null };
  pending.push(item);
  paint();
  try {
    const data = await readDataUrl(file);
    const desc = await send({ data, kind, name: item.name }, (p) => {
      item.pct = p;
      if (p >= 1 && kind !== 'photo') item.state = 'writing';
      paintProgress(item);
    });
    item.desc = desc;
    item.state = 'done';
    if (desc.transcriptError) ctx.toast(desc.transcriptError);
  } catch (e) {
    item.state = 'failed';
    item.error = e.message;
    ctx.toast(e.message, true);
  }
  paint();
}

function readDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || ''));
    r.onerror = () => rej(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });
}

/** XHR, not fetch: it is the only way to show real upload progress. */
function send(payload, onProgress) {
  return new Promise((res, rej) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/media');
    xhr.setRequestHeader('content-type', 'application/json');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText); } catch { /* not json */ }
      if (xhr.status >= 200 && xhr.status < 300) res(body);
      else rej(new Error(body.error || `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => rej(new Error('Upload failed. Check your connection.'));
    xhr.send(JSON.stringify(payload));
  });
}

/* --------------------------------------------------------------- recording */

async function startRecording() {
  if (rec) return;
  if (!navigator.mediaDevices?.getUserMedia || !('MediaRecorder' in window)) { ctx.toast('This browser cannot record audio.', true); return; }
  let stream;
  try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
  catch (e) { ctx.toast(e.name === 'NotAllowedError' ? 'Microphone permission denied.' : `Could not start the mic: ${e.message}`, true); return; }
  const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t)) || '';
  const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const r = { recorder, stream, chunks: [], started: Date.now(), discard: false, stopping: false, done: false, timer: null };
  recorder.ondataavailable = (e) => { if (e.data.size) r.chunks.push(e.data); };
  recorder.onstop = () => finishRecording(r);
  try { recorder.start(); } catch (e) { stream.getTracks().forEach((t) => t.stop()); ctx.toast(`Could not record: ${e.message}`, true); return; }
  r.timer = setInterval(() => { if (timerEl) timerEl.textContent = fmtDur((Date.now() - r.started) / 1000); }, 250);
  rec = r;
  if (timerEl) timerEl.textContent = '0:00';
  if (recBar) recBar.hidden = false;
}

/** Stop and keep it, or stop and throw it away. Safe to call when nothing is recording. */
function endRecording(discard) {
  const r = rec;
  if (!r) return;
  r.discard = discard;
  // stop() empties the recorder on a later tick, so a second Stop would finish
  // with no chunks and throw the take away. Only a discard may cut in, because
  // that one has to let the microphone go now.
  if (r.stopping) { if (discard) finishRecording(r); return; }
  r.stopping = true;
  if (r.recorder.state === 'inactive') finishRecording(r);
  else { try { r.recorder.stop(); } catch { finishRecording(r); } }
}

function finishRecording(r) {
  if (r.done) return;
  r.done = true;
  if (rec === r) rec = null;
  clearInterval(r.timer);
  r.stream.getTracks().forEach((t) => t.stop());   // the mic light goes out here
  if (recBar) recBar.hidden = true;
  if (r.discard) return;
  const type = r.recorder.mimeType || 'audio/webm';
  const blob = new Blob(r.chunks, { type });
  if (blob.size < 1200) { ctx.toast('That was too short to keep.'); return; }
  const secs = Math.round((Date.now() - r.started) / 1000);
  const ext = type.includes('mp4') ? 'm4a' : 'webm';
  add(new File([blob], `voice-note-${secs}s.${ext}`, { type }), 'audio');
}

/* ----------------------------------------------------------- pending strip */

export function pendingAttachments() { return pending.filter((x) => x.desc).map((x) => x.desc); }

export function clearPending() { pending = []; if (strip) paint(); }

function paint() {
  if (!strip) return;
  strip.innerHTML = pending.map(cardHtml).join('');
  strip.querySelectorAll('[data-rm]').forEach((b) => { b.onclick = () => removePending(b.dataset.rm); });
}

function cardHtml(it) {
  const pct = Math.round(it.pct * 100);
  let body;
  if (it.state === 'done' && it.desc) body = doneHtml(it.desc);
  else if (it.state === 'failed') body = `<p class="jm-err">${esc(it.error || 'Upload failed.')}</p>`;
  else body = `<div class="bar"><i style="width:${pct}%"></i></div><small>${it.state === 'writing' ? 'Writing it out&hellip;' : `Uploading ${pct}%`}</small>`;
  return `<div class="jm-card" data-card="${esc(it.uid)}">
      <span class="jm-kind">${esc(LABEL[it.kind] || 'File')}</span>
      <b>${esc(it.name)}</b>
      ${body}
      <button class="jm-x" data-rm="${esc(it.uid)}" title="Remove">&#x2715;</button>
    </div>`;
}

function doneHtml(d) {
  const head = d.kind === 'photo'
    ? `<img class="jm-thumb" src="${esc(mediaUrl(d))}" alt="${esc(d.name)}" loading="lazy">`
    : `<div class="jm-wave">${'<i></i>'.repeat(18)}</div>`;
  const note = d.transcriptError ? `<small class="jm-note">${esc(d.transcriptError)}</small>` : '';
  const t = String(d.transcript || '').trim();
  const tr = String(d.translation || '').trim();
  const text = t ? `<div class="jm-tx"><p>${esc(t)}</p>${tr ? `<p class="jm-tr">${esc(tr)}</p>` : ''}</div>` : '';
  return `${head}<small>${esc(fmtBytes(d.size || 0))} &middot; saved</small>${note}${text}`;
}

function paintProgress(it) {
  const card = strip && strip.querySelector(`[data-card="${it.uid}"]`);
  if (!card) return;
  const bar = card.querySelector('.bar i');
  if (bar) bar.style.width = `${Math.round(it.pct * 100)}%`;
  const lab = card.querySelector('small');
  if (lab) lab.innerHTML = it.state === 'writing' ? 'Writing it out&hellip;' : `Uploading ${Math.round(it.pct * 100)}%`;
}

async function removePending(uid) {
  const i = pending.findIndex((x) => x.uid === uid);
  if (i < 0) return;
  const [item] = pending.splice(i, 1);
  paint();
  if (!item.desc) return;
  try { await ctx.api(`/api/media/${encodeURIComponent(fileOf(item.desc))}`, { method: 'DELETE' }); }
  catch (e) { ctx.toast(e.message, true); }
}

/* ------------------------------------------------------- saved entry markup */

/**
 * Called inside a template literal for every journal entry, so it stays cheap:
 * no media element is created until the player presses play.
 */
export function attachmentsHtml(entry) {
  const list = Array.isArray(entry?.media) ? entry.media : [];
  if (!list.length) return '';
  const html = list.map(oneHtml).join('');
  return html ? `<div class="jm-att">${html}</div>` : '';
}

function oneHtml(d) {
  if (!d || typeof d !== 'object') return '';
  const src = esc(mediaUrl(d));
  const name = esc(d.name || LABEL[d.kind] || 'Attachment');
  if (d.kind === 'photo') {
    return `<figure class="jm-a photo"><img src="${src}" alt="${name}" loading="lazy" data-lb="${src}">
      <figcaption>${name}</figcaption></figure>`;
  }
  const kind = d.kind === 'video' ? 'video' : 'audio';
  return `<div class="jm-a ${kind}">
      <button class="jm-play" data-play="${src}" data-kind="${kind}">&#x25B6;<span>${name}</span></button>
      ${transcriptHtml(d)}
    </div>`;
}

function transcriptHtml(d) {
  const t = String(d.transcript || '').trim();
  const tr = String(d.translation || '').trim();
  if (!t) return '';
  return `<button class="jm-tt">Show transcript</button>
    <div class="jm-tx" hidden><p>${esc(t)}</p>${tr ? `<p class="jm-tr">${esc(tr)}</p>` : ''}</div>`;
}

/** app.js calls this after every repaint of the journal list. One delegated handler. */
export function bindEntries(listEl) {
  if (!listEl) return;
  listEl.onclick = (e) => {
    const t = e.target;
    if (!t || !t.closest) return;
    const play = t.closest('[data-play]');
    if (play) { swapInPlayer(play); return; }
    const toggle = t.closest('.jm-tt');
    if (toggle) {
      const box = toggle.nextElementSibling;
      if (!box) return;
      box.hidden = !box.hidden;
      toggle.textContent = box.hidden ? 'Show transcript' : 'Hide transcript';
      return;
    }
    const shot = t.closest('[data-lb]');
    if (shot) lightbox(shot.dataset.lb, shot.getAttribute('alt') || '');
  };
}

function swapInPlayer(btn) {
  const el = document.createElement(btn.dataset.kind === 'video' ? 'video' : 'audio');
  el.src = btn.dataset.play;
  el.controls = true;
  el.preload = 'metadata';
  el.className = 'jm-player';
  btn.replaceWith(el);
  el.play().catch(() => { /* the player can press play themselves */ });
}

/* ---------------------------------------------------------------- lightbox */

let lb = null;
function lightbox(src, name) {
  if (!lb) {
    lb = document.createElement('div');
    lb.className = 'modal jm-lb';
    lb.innerHTML = `<div class="sheet jm-lb-sheet"><img alt=""><div class="jm-lb-bar"><span></span><button class="btn">Close</button></div></div>`;
    document.body.appendChild(lb);
    const close = () => lb.classList.remove('open');
    lb.querySelector('button').onclick = close;
    lb.onclick = (e) => { if (e.target === lb) close(); };
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }
  lb.querySelector('img').src = src;
  lb.querySelector('img').alt = name;
  lb.querySelector('span').textContent = name;
  lb.classList.add('open');
}
